import {
  GUIDE_TEXT_MAX_WIDTH,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import type { SuccessSystemTopic as Topic } from '@backrs_ui/grow-ui/src/pages/SuccessSystem/successSystem.content';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type SuccessSystemTopicProps = Readonly<{
  topic: Topic;
  /** Replaces the tool card when the topic has no tool in the stack. */
  aside?: React.ReactNode;
}>;

const WHY_IT_MATTERS = 'Why it matters:';

export const SuccessSystemTopic: React.FC<SuccessSystemTopicProps> = ({ topic, aside }) => (
  <Box id={topic.id} sx={topicSx}>
    <Box sx={textColumnSx}>
      <Typography variant="text-md-semibold" sx={titleSx}>
        {topic.title}
      </Typography>
      <Typography variant="text-md-regular" sx={bodySx}>
        {topic.body}
      </Typography>
      <Box>
        <Typography variant="text-md-semibold" component="p" sx={bodySx}>
          {WHY_IT_MATTERS}
        </Typography>
        <Typography variant="text-md-regular" sx={bodySx}>
          {topic.whyItMatters}
        </Typography>
      </Box>
    </Box>
    {(topic.toolId || aside) && (
      <Box sx={asideColumnSx}>
        {topic.toolId ? (
          <GuideToolsSection
            toolIds={[topic.toolId]}
            contextLabel={topic.title}
            direction="column"
          />
        ) : (
          aside
        )}
      </Box>
    )}
  </Box>
);

const topicSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  columnGap: backrs.spacing['spacing-6xl'],
  rowGap: backrs.spacing['spacing-3xl'],
  scrollMarginTop: backrs.spacing['spacing-3xl'],
});

const textColumnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  flex: '1 1 320px',
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const asideColumnSx: SxProps<Theme> = {
  flex: '0 0 auto',
  maxWidth: '100%',
  // The nugget column is wider than its 282px card on wide screens; push the card to the right edge.
  '& [id^="nugget-list-card-"] > *': { ml: 'auto' },
};

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-secondary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
